class MainBlogLoadMore {
  constructor(container) {
    this.container = container;
    this.articleGrid = container.querySelector('[data-blog-articles]');
    this.button = container.querySelector('[data-blog-load-more-button]');
    this.loadMore = container.querySelector('[data-blog-load-more]');
    this.count = container.querySelector('[data-blog-count]');
    this.status = container.querySelector('[data-blog-status]');
    this.total = Number(container.dataset.blogTotal) || this.articleGrid?.children.length || 0;
    this.isLoading = false;

    if (!this.articleGrid) return;
    this.updateCount();
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  articleKey(article) {
    return article.dataset.articleId || article.querySelector('a[href]')?.href;
  }

  async onClick(event) {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.isLoading || !this.button.getAttribute('href')) return;

    const nextUrl = this.button.href;
    this.setLoading(true);
    this.status.textContent = '';
    this.status.classList.add('visually-hidden');

    try {
      const response = await fetch(nextUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Unable to load blog page: ${response.status}`);

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = nextDocument.querySelector('[data-blog-load-more-root]');
      if (!nextRoot) throw new Error('Blog listing not found.');

      const existingKeys = new Set(Array.from(this.articleGrid.children, (article) => this.articleKey(article)).filter(Boolean));
      const nextArticles = Array.from(nextRoot.querySelectorAll('[data-blog-articles] > .blog-articles__article')).filter((article) => {
        const key = this.articleKey(article);
        if (key && existingKeys.has(key)) return false;
        if (key) existingKeys.add(key);
        return true;
      });
      if (!nextArticles.length) throw new Error('No new blog articles found.');

      const nextButton = nextRoot.querySelector('[data-blog-load-more-button]');
      const nextHref = nextButton?.getAttribute('href');
      const nextPage = nextHref ? new URL(nextHref, response.url || nextUrl).href : null;
      const fragment = document.createDocumentFragment();
      const firstArticle = document.importNode(nextArticles[0], true);
      fragment.appendChild(firstArticle);
      nextArticles.slice(1).forEach((article) => fragment.appendChild(document.importNode(article, true)));

      const moveFocus = document.activeElement === this.button;
      this.articleGrid.appendChild(fragment);
      this.refreshScrollAnimations();
      this.updateCount();
      if (nextPage) {
        this.button.href = nextPage;
      } else {
        this.button.removeAttribute('href');
        this.loadMore.hidden = true;
      }
      this.status.textContent = this.articleGrid.children.length === this.total
        ? `All ${this.total} articles are shown.`
        : `${nextArticles.length} more articles loaded.`;
      if (moveFocus) firstArticle.querySelector('a[href]')?.focus({ preventScroll: true });
    } catch (error) {
      this.status.textContent = 'We couldn’t load more articles. Please try again.';
      this.status.classList.remove('visually-hidden');
    } finally {
      this.setLoading(false);
    }
  }

  updateCount() {
    if (this.count) this.count.textContent = `${this.articleGrid.children.length} of ${this.total} articles shown`;
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this.button.textContent = isLoading ? 'Loading…' : 'View more';
    this.button.setAttribute('aria-busy', String(isLoading));
    this.button.setAttribute('aria-disabled', String(isLoading));
  }

  refreshScrollAnimations() {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(this.articleGrid);
      return;
    }
    this.articleGrid.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.remove('scroll-trigger--offscreen');
      element.classList.add('scroll-trigger--cancel');
    });
  }
}

function initializeMainBlogLoadMore(root = document) {
  root.querySelectorAll('[data-blog-load-more-root]').forEach((container) => {
    if (container.dataset.blogLoadMoreInitialized === 'true') return;
    container.dataset.blogLoadMoreInitialized = 'true';
    new MainBlogLoadMore(container);
  });
}

document.addEventListener('DOMContentLoaded', () => initializeMainBlogLoadMore());
document.addEventListener('shopify:section:load', (event) => initializeMainBlogLoadMore(event.target));
